import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import type Stripe from 'stripe';
import { endpoint } from '../env';
import { useStats } from '../hooks/useStats';
import { useStorage } from '../hooks/useStorage';
import { teamCustomersJotaiAtom, teamsJotaiAtom } from '../jotai/atoms';
import { requestWithAuth } from '../store/requestWithAuth';
import { CustomerPortalLink } from './CustomerPortalLink';
import type { Params } from './Dashboard';

export type DashboardPaymentsProps = {};

const styles = {
  root: {
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > *': {
      marginBottom: 4
    }
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: 0.5
    }
  },
  downloadLink: {
    textDecoration: 'underline'
  }
} as const;

export function DashboardPayments(props: DashboardPaymentsProps) {
  const { teamId } = useParams<Params>();
  const teamLoadable = useAtomValue(loadable(teamsJotaiAtom(teamId)));
  const isTeamDisabled =
    teamLoadable.state === 'hasData' && teamLoadable.data?.enabled === false;

  // customerが削除されているのはおかしい
  const customerLoadable = useAtomValue(
    loadable(teamCustomersJotaiAtom(teamId))
  );
  const isCustomerDeleted =
    customerLoadable.state === 'hasData' && customerLoadable.data?.deleted;

  if (isCustomerDeleted) {
    return (
      <Box sx={styles.root}>
        <Typography variant="h6">
          お客様のアカウントに問題が発生しています。お手数ですが、ハックフォープレイ社にお問い合わせ下さい
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Quantity />
      <Typography variant="h6">お支払い情報</Typography>
      {isTeamDisabled ? (
        <Alert severity="warning">
          この度はお申し込みありがとうございます。貴社は現在、仮登録いただいている状態です。
          <br />
          アカウントを有効化するため、下記リンクよりお支払い情報のご入力をお願いいたします。
        </Alert>
      ) : null}
      <CustomerPortalLink teamId={teamId} data-testid="customer-portal-link">
        {isTeamDisabled ? 'お支払い情報を入力する' : 'お支払い情報を変更する'}
      </CustomerPortalLink>
      {!isTeamDisabled && <InvoiceDetailsSection teamId={teamId} />}
    </Box>
  );
}

function InvoiceDetailsSection({ teamId }: { teamId: string }) {
  const storage = useStorage();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const handleDownload = async (date: string) => {
    setLoading(true);
    setError(null);
    try {
      // Ensure we have the full YYYY-MM-DD.csv format
      const ref = storage.ref(`invoice_details/${teamId}/${date}.csv`);
      const url = await ref.getDownloadURL();

      // CORSエラーを回避するために直接ダウンロードリンクを開く
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.download = `invoice_details_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const [files, setFiles] = React.useState<string[]>([]);

  React.useEffect(() => {
    const fetchFiles = async () => {
      try {
        const ref = storage.ref(`invoice_details/${teamId}`);
        const result = await ref.listAll();
        // Sort files by name (YYYY-MM-DD.csv) in descending order and take the first 12
        // Filter and sort files in YYYY-MM-DD.csv format
        const sortedFiles = result.items
          .map(item => item.name)
          .filter(name => /^\d{4}-\d{2}-\d{2}\.csv$/.test(name))
          .map(name => name.replace('.csv', ''))
          .sort((a, b) => b.localeCompare(a))
          .slice(0, 12);
        setFiles(sortedFiles);
      } catch (error) {
        console.error('Error fetching invoice files:', error);
        setFiles([]);
      }
    };

    fetchFiles();
  }, [storage, teamId]);

  return (
    <>
      <Typography variant="h6">請求明細のダウンロード</Typography>
      <Box sx={styles.buttonContainer}>
        {files.map(date => (
          <Button
            key={date}
            variant="text"
            color="primary"
            sx={styles.downloadLink}
            onClick={() => handleDownload(date)}
            disabled={loading}
            data-testid="invoice-download"
          >
            {date}
          </Button>
        ))}
      </Box>
      {error && (
        <Alert severity="error">
          ダウンロードに失敗しました: {error.message}
        </Alert>
      )}
    </>
  );
}

export function Quantity() {
  // 現在の席数などを確認する
  const { teamId } = useParams<Params>();
  const { max, has } = useStats(teamId);
  const [processing, setProcessing] = React.useState(false);

  // 利用人数を更新する
  const setTeamCustomer = useSetAtom(teamCustomersJotaiAtom(teamId));
  const handleReload = React.useCallback(async () => {
    setProcessing(true);
    try {
      const customer = await requestWithAuth<Stripe.Customer>(
        endpoint + `/teamCustomers?teamId=${teamId}`
      );
      setTeamCustomer(customer);
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  }, [teamId, setTeamCustomer]);

  // 有効化前は不要な情報なので出さない
  const team = useAtomValue(teamsJotaiAtom(teamId));
  if (team?.enabled === false) {
    return null;
  }

  // リロード中
  if (processing) {
    return <CircularProgress />;
  }

  return (
    <div>
      <Typography variant="body1">
        現在の有効利用人数{max === undefined ? 'を取得中' : `は ${max} 人`}です
      </Typography>
      {max !== has ? (
        <Alert severity="info">
          利用人数が変更されている可能性があります。
          <Button variant="contained" color="primary" onClick={handleReload}>
            再読み込み
          </Button>
        </Alert>
      ) : null}
    </div>
  );
}
